// frontend/src/services/ffmpegService.ts
// Lazy-loads ffmpeg.wasm and transcodes a video at a given playback speed.
// The WASM core (~31 MB) is fetched from the official CDN on first use only.
//
// ffmpeg.wasm 0.12.x runs in a Web Worker and requires SharedArrayBuffer.
// SharedArrayBuffer needs Cross-Origin-Isolation headers (COOP + COEP).
// If those headers are absent the worker falls back to a single-threaded build
// automatically — transcoding will still work, just slower.
//
// Video bytes are fetched via the backend /api/content?key= proxy rather than
// directly from the S3 presigned URL. Direct S3 fetches are blocked by CORS
// because S3 does not include Access-Control-Allow-Origin on presigned URLs
// unless the bucket CORS policy explicitly allows the requesting origin.

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { baseURL } from './api';

// CDN base for the 0.12.x WASM core
const CDN = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';

export interface TranscodeProgress {
  /** 0–1 fraction of the video processed, or -1 while loading WASM */
  ratio: number;
  phase: 'loading' | 'fetching' | 'transcoding' | 'done' | 'error';
  message: string;
}

export type ProgressCallback = (p: TranscodeProgress) => void;

// Singleton — reuse across calls so the WASM core is only loaded once per session
let ffmpegInstance: FFmpeg | null = null;
let loadPromise: Promise<void> | null = null;

async function getFFmpeg(onProgress: ProgressCallback): Promise<FFmpeg> {
  if (ffmpegInstance?.loaded) return ffmpegInstance;

  if (!ffmpegInstance) {
    ffmpegInstance = new FFmpeg();
  }

  if (!loadPromise) {
    onProgress({ ratio: -1, phase: 'loading', message: 'Loading FFmpeg WASM (~31 MB, first use only)…' });

    loadPromise = (async () => {
      const coreURL = await toBlobURL(`${CDN}/ffmpeg-core.js`, 'text/javascript');
      const wasmURL = await toBlobURL(`${CDN}/ffmpeg-core.wasm`, 'application/wasm');

      await ffmpegInstance!.load({ coreURL, wasmURL });
    })().catch((err) => {
      // Reset so the next call retries
      loadPromise = null;
      ffmpegInstance = null;
      throw err;
    });
  }

  await loadPromise;
  return ffmpegInstance!;
}

/**
 * Download a video, recode it at `speed`× using the `atempo` filter chain,
 * and trigger a browser download of the result.
 *
 * The video is fetched via the backend /api/content proxy to avoid CORS
 * restrictions on direct S3 presigned URL fetches.
 *
 * @param s3Key      S3 object key (e.g. "folder/file.mp4")
 * @param filename   Suggested download filename (without extension)
 * @param speed      Playback speed multiplier (0.5 – 12)
 * @param onProgress Called repeatedly with progress updates
 * @param signal     AbortSignal to cancel mid-transcode
 */
export async function transcodeAndDownload(
  s3Key: string,
  filename: string,
  speed: number,
  onProgress: ProgressCallback,
  signal?: AbortSignal,
): Promise<void> {
  // ── 1. Load WASM ──────────────────────────────────────────────────────────
  let ffmpeg: FFmpeg;
  try {
    ffmpeg = await getFFmpeg(onProgress);
  } catch (err) {
    onProgress({ ratio: -1, phase: 'error', message: 'Failed to load FFmpeg. Check your connection and try again.' });
    throw err;
  }

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  // ── 2. Fetch source video via backend proxy (avoids S3 CORS restriction) ──
  // Direct S3 presigned URLs are blocked by CORS when fetched with fetch().
  // The /api/content endpoint proxies the bytes through the Worker, which
  // adds the correct Access-Control-Allow-Origin header.
  onProgress({ ratio: 0, phase: 'fetching', message: 'Fetching video…' });

  const proxyUrl = `${baseURL}/api/content?key=${encodeURIComponent(s3Key)}`;

  let inputData: Uint8Array;
  try {
    inputData = await fetchFile(proxyUrl);
  } catch (err) {
    onProgress({ ratio: -1, phase: 'error', message: 'Failed to fetch video. Please try again.' });
    throw err;
  }

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const inputName = 'input.mp4';
  const outputName = 'output.mp4';

  await ffmpeg.writeFile(inputName, inputData);

  // ── 3. Build atempo filter chain ──────────────────────────────────────────
  // atempo only accepts values in [0.5, 2.0], so chain multiple filters for
  // speeds outside that range. e.g. 8× = atempo=2,atempo=2,atempo=2
  const atempoFilters = buildAtempoChain(speed);

  // ── 4. Wire up progress reporting ─────────────────────────────────────────
  const handleProgress = ({ progress }: { progress: number }) => {
    if (signal?.aborted) return;
    onProgress({
      ratio: Math.min(progress, 1),
      phase: 'transcoding',
      message: `Transcoding at ${speed}×… ${Math.round(Math.min(progress, 1) * 100)}%`,
    });
  };
  ffmpeg.on('progress', handleProgress);

  // ── 5. Transcode ──────────────────────────────────────────────────────────
  onProgress({ ratio: 0, phase: 'transcoding', message: `Starting transcode at ${speed}×…` });

  try {
    await ffmpeg.exec([
      '-i', inputName,
      '-filter:v', `setpts=${(1 / speed).toFixed(6)}*PTS`,
      '-filter:a', atempoFilters,
      '-c:v', 'libx264',
      '-preset', 'ultrafast', // prioritise speed over file size
      '-crf', '23',
      '-c:a', 'aac',
      outputName,
    ]);
  } catch (err) {
    ffmpeg.off('progress', handleProgress);
    onProgress({ ratio: -1, phase: 'error', message: 'Transcode failed. The video format may not be supported.' });
    throw err;
  }

  ffmpeg.off('progress', handleProgress);

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  // ── 6. Read output and trigger download ───────────────────────────────────
  onProgress({ ratio: 1, phase: 'done', message: 'Preparing download…' });

  const outputData = await ffmpeg.readFile(outputName) as Uint8Array;
  const blob = new Blob([outputData], { type: 'video/mp4' });
  const url = URL.createObjectURL(blob);

  const speedLabel = speed === Math.floor(speed) ? `${speed}x` : `${speed}x`;
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${speedLabel}.mp4`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke after a short delay to let the browser start the download
  setTimeout(() => URL.revokeObjectURL(url), 10_000);

  // Clean up WASM virtual FS
  await ffmpeg.deleteFile(inputName).catch(() => {});
  await ffmpeg.deleteFile(outputName).catch(() => {});
}

/**
 * Build a chained atempo filter string for speeds outside [0.5, 2.0].
 * Examples:
 *   0.25 → "atempo=0.5,atempo=0.5"
 *   4    → "atempo=2.0,atempo=2.0"
 *   12   → "atempo=2.0,atempo=2.0,atempo=2.0,atempo=1.5"
 */
function buildAtempoChain(speed: number): string {
  const filters: string[] = [];
  let remaining = speed;

  // For speeds > 2, chain atempo=2.0 until remainder fits in [0.5, 2.0]
  while (remaining > 2.0) {
    filters.push('atempo=2.0');
    remaining /= 2.0;
  }
  // For speeds < 0.5, chain atempo=0.5 until remainder fits in [0.5, 2.0]
  while (remaining < 0.5) {
    filters.push('atempo=0.5');
    remaining /= 0.5;
  }

  // Final stage with the remainder (rounded to avoid floating point noise)
  const final = Math.round(remaining * 1000) / 1000;
  if (Math.abs(final - 1.0) > 0.001) {
    filters.push(`atempo=${final}`);
  } else if (filters.length === 0) {
    // speed === 1 exactly — no audio filter needed but ffmpeg requires one
    filters.push('atempo=1.0');
  }

  return filters.join(',');
}
